import React, { useEffect, useState } from 'react';
import { GOOGLE_FAVICON_HOST_RE, buildLogoSources } from '../../../utils/urlMetadata';

// Resolved-position cache. Key: the ordered source chain itself, value: the
// index it settled on — the source that actually rendered, or one past the end
// when every source failed. The fallback index is per-mount component state, so
// without this a remount replays the chain from source 0 and every row visibly
// blinks through its dud sources again (leaving a list for a drawer and coming
// back is the common case — the first source is the site's own /favicon.ico,
// which 404s for many companies).
// The key is derived from public logo/favicon URLs and carries no org identity,
// so it needs no per-investor scoping.
// Entries expire so a position reached because of a transient network failure
// can recover — otherwise one offline blip pins a company to its initials for
// the whole module lifetime.
const RESOLVED_INDEX_TTL_MS = 5 * 60 * 1000;
const resolvedIndexCache = new Map<string, { index: number; expiresAt: number }>();

const getResolvedIndex = (key: string): number => {
    const hit = resolvedIndexCache.get(key);
    if (!hit || hit.expiresAt <= Date.now()) {
        resolvedIndexCache.delete(key);
        return 0;
    }
    return hit.index;
};

/**
 * Unified company-logo hook for any surface: resolves the ordered source chain
 * from the directly-provided `logo`/`websiteUrl` and drives the
 * render-fallback state machine (advance on <img> error; drop a Google favicon
 * that loads as the generic globe at or under `genericFaviconMaxSizePx`) in one
 * place. No network fetches: when the surface has no logo/website, callers
 * render their default first-letter badge.
 */
export function useCompanyLogo(params: { logo?: string; websiteUrl?: string; genericFaviconMaxSizePx?: number }): {
    currentSource: string | undefined;
    handleError: () => void;
    handleLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
} {
    const { logo, websiteUrl, genericFaviconMaxSizePx = 16 } = params;

    const sources = buildLogoSources(logo, websiteUrl);

    // Render-fallback state machine over the resolved sources: advance on
    // <img> error, resume at the position this chain already settled on
    // whenever the list changes by content, and treat a small Google favicon
    // load as a miss.
    const sourcesKey = sources.join('|');

    const [index, setIndex] = useState(() => getResolvedIndex(sourcesKey));

    useEffect(() => {
        setIndex(getResolvedIndex(sourcesKey));
    }, [sourcesKey]);

    // Record where the chain has got to, so the next mount of this same chain
    // starts here rather than walking the failures again. Skipped when the
    // cache already holds this position, so revisiting a list doesn't keep
    // pushing a failed position's expiry out of reach.
    useEffect(() => {
        if (!sourcesKey || index === 0 || getResolvedIndex(sourcesKey) === index) return;
        resolvedIndexCache.set(sourcesKey, { index, expiresAt: Date.now() + RESOLVED_INDEX_TTL_MS });
    }, [sourcesKey, index]);

    const currentSource = sources[index];

    const advance = () => setIndex((i) => i + 1);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        // Google's favicon service returns a 16x16 generic globe when it has no
        // real favicon for the domain. That loads successfully, so onError never
        // fires — detect the tell-tale small globe and fall through to the next
        // source (ultimately the first-letter badge) instead of showing it.
        const img = e.currentTarget;
        const isGoogleFavicon = GOOGLE_FAVICON_HOST_RE.test(currentSource ?? '');
        if (isGoogleFavicon && img.naturalWidth > 0 && img.naturalWidth <= genericFaviconMaxSizePx) {
            advance();
        }
    };

    return { currentSource, handleError: advance, handleLoad };
}
